import React, { useCallback, useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';

import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';

import IconChevronLeft from '@/assets/icons/icon-arrow-left.svg?react';
import IconChevronRight from '@/assets/icons/icon-arrow-right.svg?react';

export interface DateRange {
  start: Date;
  end: Date;
}

interface CustomRangeCalendarProps {
  value?: DateRange;
  onApply?: (range: DateRange) => void;
  onCancel?: () => void;
  onSelectionChange?: (range: DateRange | null) => void;
  className?: string;
}

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isInRange = (day: Date, start: Date, end: Date) => {
  const t = day.getTime();
  const s = Math.min(start.getTime(), end.getTime());
  const e = Math.max(start.getTime(), end.getTime());
  return t > s && t < e;
};

const formatMonthYear = (date: Date) =>
  date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

/**
 * Build a Monday-start calendar grid for a given month.
 * Returns exactly the number of day-cells needed (5 or 6 rows of 7).
 */
const getCalendarDays = (year: number, month: number) => {
  const firstDay = new Date(year, month, 1);
  // JS getDay(): 0=Sun … 6=Sat → convert to Mon=0
  let startOffset = firstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days: { date: Date; isCurrentMonth: boolean }[] = [];

  // Previous-month overflow
  for (let i = startOffset - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, isCurrentMonth: false });
  }

  // Current month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({ date: new Date(year, month, i), isCurrentMonth: true });
  }

  // Next-month overflow to complete the last row
  const remaining = 7 - (days.length % 7);
  if (remaining < 7) {
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
  }

  return days;
};

export const CustomRangeCalendar: React.FC<CustomRangeCalendarProps> = ({
  value,
  onApply,
  onCancel,
  onSelectionChange,
  className,
}) => {
  const [viewMonth, setViewMonth] = useState(value?.start.getMonth() ?? new Date().getMonth());
  const [viewYear, setViewYear] = useState(value?.start.getFullYear() ?? new Date().getFullYear());
  const [rangeStart, setRangeStart] = useState<Date | null>(value?.start ?? null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(value?.end ?? null);
  const [selecting, setSelecting] = useState(false);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  useEffect(() => {
    setRangeStart(value?.start ?? null);
    setRangeEnd(value?.end ?? null);
    if (value?.start) {
      setViewMonth(value.start.getMonth());
      setViewYear(value.start.getFullYear());
    }
    setSelecting(false);
  }, [value]);

  useEffect(() => {
    if (rangeStart && rangeEnd && !selecting) {
      const start = rangeStart < rangeEnd ? rangeStart : rangeEnd;
      const end = rangeStart < rangeEnd ? rangeEnd : rangeStart;
      onSelectionChange?.({ start, end });
    }
  }, [rangeStart, rangeEnd, selecting, onSelectionChange]);

  const days = useMemo(() => getCalendarDays(viewYear, viewMonth), [viewYear, viewMonth]);
  const weeks = useMemo(() => {
    const result: (typeof days)[] = [];
    for (let i = 0; i < days.length; i += 7) {
      result.push(days.slice(i, i + 7));
    }
    return result;
  }, [days]);

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handleDayClick = (date: Date) => {
    if (!selecting) {
      setRangeStart(date);
      setRangeEnd(null);
      setSelecting(true);
      setHoverDate(null);
    } else {
      const s = rangeStart!;
      if (date < s) {
        setRangeStart(date);
        setRangeEnd(s);
      } else {
        setRangeEnd(date);
      }
      setSelecting(false);
      setHoverDate(null);
    }
  };

  const handleApply = () => {
    if (rangeStart && rangeEnd) {
      const start = rangeStart < rangeEnd ? rangeStart : rangeEnd;
      const end = rangeStart < rangeEnd ? rangeEnd : rangeStart;
      onApply?.({ start, end });
    }
  };

  const getDayState = useCallback(
    (date: Date): 'start' | 'end' | 'in-range' | 'default' => {
      const effectiveEnd = selecting ? hoverDate : rangeEnd;

      if (rangeStart && isSameDay(date, rangeStart)) return 'start';
      if (effectiveEnd && isSameDay(date, effectiveEnd)) return 'end';
      if (rangeStart && effectiveEnd && isInRange(date, rangeStart, effectiveEnd))
        return 'in-range';
      return 'default';
    },
    [rangeStart, rangeEnd, selecting, hoverDate],
  );

  const getConnector = useCallback(
    (date: Date): 'left' | 'right' | 'both' | 'none' => {
      const effectiveEnd = selecting ? hoverDate : rangeEnd;
      if (!rangeStart || !effectiveEnd) return 'none';

      const lo = rangeStart < effectiveEnd ? rangeStart : effectiveEnd;
      const hi = rangeStart < effectiveEnd ? effectiveEnd : rangeStart;

      if (isSameDay(lo, hi)) return 'none';

      const state = getDayState(date);

      if (state === 'start') {
        if (isSameDay(date, lo)) return 'right';
        return 'left';
      }
      if (state === 'end') {
        if (isSameDay(date, hi)) return 'left';
        return 'right';
      }
      if (state === 'in-range') return 'both';

      return 'none';
    },
    [rangeStart, rangeEnd, selecting, hoverDate, getDayState],
  );

  return (
    <div
      className={clsx(
        'bg-neutral-0 flex w-full flex-col gap-5 overflow-hidden rounded-lg p-3 shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)]',
        'min-w-56',
        className,
      )}
    >
      {/* Month navigation */}
      <div className="flex flex-col gap-3">
        <div className="flex h-8 items-center justify-between px-2">
          <IconSimpleButton
            icon={IconChevronLeft}
            size={24}
            noBgWhenDefault
            className="border border-neutral-100"
            onClick={handlePrevMonth}
          />
          <span className="text-center text-sm leading-5 font-medium text-neutral-500">
            {formatMonthYear(new Date(viewYear, viewMonth))}
          </span>
          <IconSimpleButton
            icon={IconChevronRight}
            size={24}
            noBgWhenDefault
            className="border border-neutral-100"
            onClick={handleNextMonth}
          />
        </div>

        {/* Day grid */}
        <div className="flex flex-col">
          {/* Day-of-week headers */}
          <div className="flex">
            {DAY_LABELS.map((label, i) => (
              <div key={i} className="flex h-7 flex-1 items-center justify-center p-1.5">
                <span className="text-center text-[10px] leading-5 font-medium text-neutral-500">
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* Weeks */}
          {weeks.map((week, wi) => (
            <div key={wi} className="m-0.5 flex">
              {week.map((day, di) => {
                const state = getDayState(day.date);
                const connector = getConnector(day.date);
                const isEndpoint = state === 'start' || state === 'end';

                return (
                  <div key={di} className="relative flex h-7 flex-1 items-center justify-center">
                    {/* Connector background */}
                    {connector !== 'none' && (
                      <div
                        className={clsx(
                          'pointer-events-none absolute inset-y-0 bg-indigo-100',
                          connector === 'both' && 'right-0 left-0',
                          connector === 'right' && 'right-0 left-1/2',
                          connector === 'left' && 'right-1/2 left-0',
                          di === 0 && 'left-1 rounded-l-full',
                          di === 6 && 'right-1 rounded-r-full',
                        )}
                      />
                    )}
                    {/* Day button */}
                    <button
                      type="button"
                      onClick={() => handleDayClick(day.date)}
                      onMouseEnter={() => selecting && setHoverDate(day.date)}
                      className={clsx(
                        'relative z-10 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full p-1.5 transition-colors',
                        isEndpoint && 'bg-indigo-500 shadow-[0_0_0_2px_rgba(99,91,255,0.3)]',
                        state === 'in-range' && 'bg-indigo-100',
                        state === 'default' && 'hover:bg-neutral-200',
                      )}
                    >
                      <span
                        className={clsx(
                          'text-center text-[10px] leading-5 font-semibold',
                          isEndpoint && 'text-white',
                          state === 'in-range' && 'text-neutral-900',
                          state === 'default' && day.isCurrentMonth && 'text-neutral-900',
                          state === 'default' && !day.isCurrentMonth && 'text-neutral-400',
                        )}
                      >
                        {day.date.getDate()}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Footer buttons */}
      <div className="flex gap-2">
        <SecondaryButton
          text="Cancel"
          icon={null}
          size="sm"
          onClick={onCancel}
          className="h-9 flex-1"
        />
        <PrimaryButton
          text="Apply"
          icon={null}
          size="sm"
          onClick={handleApply}
          className="h-9 flex-1"
        />
      </div>
    </div>
  );
};
